/**
 * A "What's new" copy opened from Tasks (the desktop drawer and the iPad's
 * Tasks card use the same drawer): the note itself and Got it. No Done, no
 * reply box, no hand-back line.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { RecipientNote } from "@/lib/whats-new";
import { NoteView } from "./note-view";

export function DrawerNote({ taskId, onDone }: { taskId: string; onDone: () => void }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["whats-new", "copy", taskId],
        queryFn: () => api.get<{ note: RecipientNote }>(`/api/whats-new/tasks/${taskId}`),
    });
    useEffect(() => {
        api.post(`/api/whats-new/tasks/${taskId}/opened`).catch(() => {});
    }, [taskId]);
    const gotIt = useMutation({
        mutationFn: () => api.post(`/api/whats-new/tasks/${taskId}/got-it`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["tasks"] });
            void qc.invalidateQueries({ queryKey: ["task-inbox"] });
            void qc.invalidateQueries({ queryKey: ["task", taskId] });
            onDone();
        },
        onError: () => toast.error("Couldn't save that. Try again."),
    });
    const note = q.data?.note;
    if (!note)
        return (
            <div className="p-6 text-sm text-muted-foreground">
                {q.isError ? "This note wouldn't open." : "Loading…"}
            </div>
        );
    return (
        <div className="p-4 pt-10">
            <NoteView
                compact
                title={note.title}
                steps={note.steps}
                senderName={note.senderName}
                sentAt={note.sentAt}
                gotIt={note.gotIt}
                busy={gotIt.isPending}
                onGotIt={() => gotIt.mutate()}
            />
        </div>
    );
}
